import { useState } from 'react';
import { Sparkles, Lightbulb, Check, Copy, RefreshCw } from 'lucide-react';
import { aiAPI } from '../services/api';

// Example descriptions for reference
const EXAMPLE_DESCRIPTIONS = [
  {
    company: "Innovent Biologics",
    description: "Innovent Biologics is a clinical-stage biopharmaceutical company focused on developing innovative oncology and autoimmune therapies. The company's proprietary technology platforms include next-generation bispecific antibodies and ADCs, with multiple assets in late-stage clinical development targeting solid tumors and hematological malignancies.",
  },
  {
    company: "Zai Lab",
    description: "Zai Lab is a commercial-stage biopharmaceutical company focused on bringing transformative medicines to patients in Greater China. With a proven track record in successful in-licensing and internal R&D, the company specializes in oncology, autoimmune diseases, and infectious diseases.",
  },
  {
    company: "Ascletis Pharma",
    description: "Ascletis Pharma is a hepatitis and HIV-focused biopharmaceutical company with an integrated R&D platform spanning small molecules to biologics. The company has successfully commercialized novel therapies in China and is expanding its pipeline into oncology and metabolic diseases.",
  },
];

export function DescriptionHelper({
  value,
  onChange,
  companyData,
  disabled = false,
}) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedDescription, setGeneratedDescription] = useState(null);
  const [error, setError] = useState(null);
  const [showExamples, setShowExamples] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError(null);
    setGeneratedDescription(null);

    try {
      const response = await aiAPI.generateDescription({
        company_name: companyData.company_name,
        stage: companyData.stage,
        therapeutic_areas: companyData.therapeutic_areas,
        tech_platforms: companyData.tech_platforms,
        lead_asset_stage: companyData.lead_asset_stage,
        country: companyData.country,
        founded: companyData.founded,
        employees: companyData.employees,
        funding_stage: companyData.funding_stage,
        existing_description: value,
      });

      if (response.data?.success) {
        setGeneratedDescription(response.data.data.description);
      } else {
        throw new Error(response.data?.message || 'Failed to generate description');
      }
    } catch (err) {
      console.error('AI generation failed:', err);
      setError(err.response?.data?.message || err.message || 'Failed to generate description');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUseGenerated = () => {
    if (generatedDescription) {
      onChange(generatedDescription);
      setGeneratedDescription(null);
    }
  };

  const handleCopyExample = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const canGenerate = companyData.company_name &&
    (companyData.therapeutic_areas?.length > 0 || companyData.tech_platforms?.length > 0);

  return (
    <div className="space-y-3">
      {/* Label with Examples Toggle */}
      <div className="flex items-center justify-between">
        <label className="label mb-0">Company Description</label>
        <button
          type="button"
          onClick={() => setShowExamples(!showExamples)}
          className="text-xs text-purple-600 hover:text-purple-700 flex items-center gap-1"
        >
          <Lightbulb className="w-3 h-3" />
          {showExamples ? 'Hide Examples' : 'See Examples'}
        </button>
      </div>

      {/* Examples Panel */}
      {showExamples && (
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 border border-purple-100">
          <p className="text-xs text-purple-700 font-medium mb-3">
            Example Descriptions (click to copy)
          </p>
          <div className="space-y-3">
            {EXAMPLE_DESCRIPTIONS.map((example, index) => (
              <div
                key={index}
                onClick={() => handleCopyExample(example.description)}
                className="bg-white rounded-lg p-3 border border-purple-100 cursor-pointer hover:border-purple-300 transition-colors group"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-gray-700">
                    {example.company}
                  </span>
                  <Copy className="w-3 h-3 text-gray-400 group-hover:text-purple-600" />
                </div>
                <p className="text-xs text-gray-600 leading-relaxed">
                  {example.description}
                </p>
              </div>
            ))}
          </div>
          {copied && (
            <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
              <Check className="w-3 h-3" /> Copied to clipboard
            </p>
          )}
        </div>
      )}

      {/* Writing Tips */}
      <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
        <p className="text-xs text-gray-600">
          <span className="font-medium text-gray-700">Tips:</span> Include your
          <span className="text-purple-600"> core focus</span>,
          <span className="text-blue-600"> technology platform</span>, and
          <span className="text-green-600"> key differentiators</span>.
          Keep it concise (2-3 sentences) for busy pharma BD executives.
        </p>
      </div>

      {/* Textarea with AI Button */}
      <div className="relative">
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="input min-h-[120px] pr-4 pb-12"
          placeholder="Describe your company's focus, capabilities, and what makes you unique..."
        />

        {/* AI Generate Button */}
        <div className="absolute bottom-3 right-3">
          <button
            type="button"
            onClick={handleGenerate}
            disabled={isGenerating || !canGenerate || disabled}
            className={`
              flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium
              transition-all shadow-sm
              ${canGenerate && !disabled
                ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }
            `}
          >
            {isGenerating ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                AI Draft
              </>
            )}
          </button>
        </div>
      </div>

      {/* Requirement Hint */}
      {!canGenerate && (
        <p className="text-xs text-amber-600">
          Fill in Company Name and at least one Therapeutic Area or Technology Platform to enable AI generation.
        </p>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Generated Description Preview */}
      {generatedDescription && (
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-purple-700 flex items-center gap-1">
              <Sparkles className="w-3 h-3" />
              AI Generated Draft
            </span>
          </div>
          <p className="text-sm text-gray-700 mb-3 leading-relaxed">
            {generatedDescription}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleUseGenerated}
              className="flex items-center gap-1 px-3 py-1.5 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors"
            >
              <Check className="w-4 h-4" />
              Use This
            </button>
            <button
              type="button"
              onClick={handleGenerate}
              disabled={isGenerating}
              className="flex items-center gap-1 px-3 py-1.5 bg-white text-purple-600 text-sm rounded-lg border border-purple-200 hover:bg-purple-50 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${isGenerating ? 'animate-spin' : ''}`} />
              Regenerate
            </button>
            <button
              type="button"
              onClick={() => setGeneratedDescription(null)}
              className="px-3 py-1.5 text-gray-500 text-sm hover:text-gray-700 transition-colors"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DescriptionHelper;
